import { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Camera, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function PhotoTimeline({ chestId }) {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/hope-chests/${chestId}/photos`);
      setPhotos(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load photos");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chestId]);

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/hope-chests/${chestId}/photos?caption=${encodeURIComponent(caption)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Moment shared");
      setOpen(false); setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/hope-chests/${chestId}/photos/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  const isPremium = user?.subscription_tier === "paid";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-xl">Today's moments</h2>
          <p className="text-xs text-[#7A736E]">A photo a day keeps the family close. <span className="text-[#3A5A4A]">Premium</span></p>
        </div>
        {isPremium ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="add-photo-button">
                <Camera className="w-4 h-4 mr-2" /> Add photo
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Share a moment</DialogTitle>
                <DialogDescription>One photo, a few words. That's all it takes.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Photo *</Label>
                  <input ref={fileRef} type="file" accept="image/*" className="block mt-1.5 text-sm" data-testid="photo-input" />
                </div>
                <div>
                  <Label>Caption</Label>
                  <Input value={caption} maxLength={280} onChange={(e) => setCaption(e.target.value)} placeholder="Mom's first walk in the garden today" className="rounded-xl mt-1.5" data-testid="photo-caption" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={busy} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="photo-submit">
                  {busy ? "Sharing…" : "Share"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Button onClick={() => window.location.href = "/pricing"} variant="outline" className="rounded-full border-[#3A5A4A] text-[#3A5A4A]" data-testid="photo-upgrade">
            Upgrade to share photos
          </Button>
        )}
      </div>

      {error && error.includes("Premium") ? (
        <div className="hc-card p-12 text-center bg-gradient-to-br from-[#F3EFEA] to-[#FCFAF8]">
          <ImageIcon className="w-12 h-12 text-[#C87B67] mx-auto mb-4" />
          <h3 className="font-display text-2xl mb-2">Save the small moments.</h3>
          <p className="text-[#7A736E] max-w-md mx-auto mb-6">A garden walk. A favorite meal. A grandkid's drawing. Premium unlocks a family photo timeline that keeps everyone close.</p>
          <Button onClick={() => window.location.href = "/pricing"} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="photo-upgrade-cta">See Premium</Button>
        </div>
      ) : photos.length === 0 ? (
        <div className="hc-card p-10 text-center text-[#7A736E]" data-testid="empty-photos">
          {isPremium ? "No photos yet. Share the first moment." : "Photos appear here once Premium is active."}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {photos.map((p) => (
            <div key={p.id} className="hc-card overflow-hidden group" data-testid={`photo-${p.id}`}>
              <div className="aspect-square bg-[#F3EFEA] relative">
                <img src={`${API}/photos/${p.id}/raw`} alt={p.caption || ""} className="w-full h-full object-cover" loading="lazy" />
                <button onClick={() => remove(p.id)} className="absolute top-2 right-2 p-2 rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition" data-testid={`photo-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-[#C87B67]" />
                </button>
              </div>
              {p.caption && <p className="px-4 py-3 text-sm">{p.caption}</p>}
              <p className="px-4 pb-3 text-xs text-[#7A736E]">{p.created_by_name && `${p.created_by_name} • `}{new Date(p.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
